import { get, patch, post } from 'core_main/request';
import {
  GetAllArticlesParameters,
  GetArticleByIdParameters,
  GetArticleBySlugParameters,
} from 'core_main/sdk/get_articles/types';

import {
  validateArticlesSearchParameters,
  validateArticleByIdParameters,
  makeArticlesSearchEndpoint,
  makeArticleByIdEndpoint,
  makePostArticleRatingEndpoint,
  makePatchArticleFeedbackEndpoint,
  makeFetchFeatureArticlesEnpoint,
  makeAllArticlesEndpoint,
} from './helpers';
import {
  ArticleApiResponse,
  ArticleFeedbackResponseData,
  ArticlesSearchParameters,
  FetchFeaturedArticlesParameters,
  PatchArticleFeedbackParameters,
  PostArticleRatingParameters,
} from './types';

export const fetchArticleById = (parameters: GetArticleByIdParameters) => {
  const validationError = validateArticleByIdParameters(parameters);

  if (validationError) {
    return Promise.reject(validationError);
  }

  return get(makeArticleByIdEndpoint(parameters));
};

export const fetchAllArticles = (parameters: GetAllArticlesParameters) => {
  return get(
    makeAllArticlesEndpoint({
      lang: parameters.lang,
      knowledgeBaseId: parameters.knowledgeBaseId,
      page: parameters.page,
    }),
  );
};

export const fetchArticleBySlug = (
  parameters: GetArticleBySlugParameters,
  page = 1,
): Promise<ArticleApiResponse | null> => {
  return fetchAllArticles({
    lang: parameters.lang,
    knowledgeBaseId: parameters.knowledgeBaseId,
    page,
  }).then((res) => {
    const foundArticle = res.data.find((article) => {
      return (
        article.attributes.slug === parameters.articleSlug &&
        article.attributes.hash === parameters.articleHash
      );
    });
    if (foundArticle) {
      return foundArticle;
    }
    if (res?.links?.next) {
      return fetchArticleBySlug(parameters, page + 1);
    }
    return null;
  });
};

export const searchArticlesByKnowledgeBaseId = (
  parameters: ArticlesSearchParameters,
) => {
  const validationError = validateArticlesSearchParameters(parameters);

  if (validationError) {
    return Promise.reject(validationError);
  }

  return get(makeArticlesSearchEndpoint(parameters));
};

export const postArticleRating = (
  parameters: PostArticleRatingParameters,
): Promise<ArticleFeedbackResponseData> => {
  return post(
    makePostArticleRatingEndpoint(parameters.articleId, parameters.lang),
    {
      body: JSON.stringify({
        rating: parameters.rating,
        knowledgeBaseId: parameters.knowledgeBaseId,
      }),
    },
  ).then((res) => res.data);
};

export const patchArticleFeedback = (
  parameters: PatchArticleFeedbackParameters,
): Promise<ArticleFeedbackResponseData> => {
  return patch(
    makePatchArticleFeedbackEndpoint(parameters.articleId, parameters.id),
    {
      body: JSON.stringify({
        suggestedReasons: parameters.suggestedReasons,
        writtenFeedback: parameters.writtenFeedback,
      }),
    },
  ).then((res) => {
    return res.data;
  });
};

export const getFeaturedArticlesForKnowledgeBase = (
  parameters: FetchFeaturedArticlesParameters,
) => {
  return get(
    makeFetchFeatureArticlesEnpoint(
      parameters.knowledgeBaseId,
      parameters?.lang,
    ),
  ).then((res) => res.data);
};
